import { useEffect, useRef } from 'react';

const NUM_PARTICLES = 1100;
const SPHERE_RADIUS_DESKTOP = 358;
const SPHERE_RADIUS_MOBILE = 215;
const FOCAL_LENGTH = 600;
const MD_BREAKPOINT = 768;
const ASCII_CHARS =
  '.,-~:;=!*#$@%&+/|\\<>[]{}()ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const SHUFFLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*';

const ROLLING_TEXTS = [
  '5-Star Service',
  '5% Management Fee',
  '55% Placement Fee',
];

function getHighlightLength(text: string) {
  const match = text.match(/^(55%|5%|5)/);
  return match?.[1]?.length ?? 0;
}

function getFormattedHTML(text: string) {
  return text.replace(/^(55%|5%|5)/, '<span style="color:#3EE4A9">$1</span>');
}

function getFormattedShuffleHTML(text: string, highlightLen: number) {
  if (highlightLen <= 0) return text;
  const highlighted = text.slice(0, highlightLen);
  const rest = text.slice(highlightLen);
  return `<span style="color:#3EE4A9">${highlighted}</span>${rest}`;
}

export function ParticleSphere() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const textOverlay = textRef.current;
    if (!canvas || !container || !textOverlay) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Seeded rather than left unassigned: `animate` sets it before the reduced
    // motion branch below cancels it, but only at its very last statement.
    let animationId = 0;
    let width: number;
    let height: number;
    let isMobile = false;

    const particles: {
      x: number;
      y: number;
      z: number;
      size: number;
      char: string;
      isBlurred: boolean;
    }[] = [];

    const projected: {
      x: number;
      y: number;
      z: number;
      scale: number;
      size: number;
      char: string;
      isBlurred: boolean;
    }[] = [];

    const rx = -0.2;
    let ry = 0;

    function resize() {
      const rect = container!.getBoundingClientRect();
      width = canvas!.width = rect.width;
      height = canvas!.height = rect.height;
      isMobile = width < MD_BREAKPOINT;
    }

    function initParticles() {
      const phi = Math.PI * (3 - Math.sqrt(5));

      for (let i = 0; i < NUM_PARTICLES; i++) {
        const y = 1 - (i / (NUM_PARTICLES - 1)) * 2;
        const radiusAtY = Math.sqrt(1 - y * y);
        const theta = phi * i;

        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;

        const offset = 1 + (Math.random() - 0.5) * 0.15;
        const charIndex = Math.floor(Math.random() * ASCII_CHARS.length);
        const isBlurred = Math.random() < 0.3;

        particles.push({
          x: x * offset,
          y: y * offset,
          z: z * offset,
          size: Math.random() * 3.5 + 2.8,
          // `charIndex` is `floor(random() * length)`, so it is always in range —
          // as are the other fixed-array reads in this file, all of them either
          // bounded by the array's own length or taken modulo it.
          char: ASCII_CHARS[charIndex]!,
          isBlurred,
        });

        projected.push({
          x: 0,
          y: 0,
          z: 0,
          scale: 0,
          size: 0,
          char: '',
          isBlurred: false,
        });
      }
    }

    function animate() {
      ctx!.clearRect(0, 0, width, height);

      const time = Date.now() * 0.0002;
      const sphereRadius = isMobile
        ? SPHERE_RADIUS_MOBILE
        : SPHERE_RADIUS_DESKTOP;
      const pulseScale = 1 + Math.sin(time * 5) * 0.06;
      const currentRadius = sphereRadius * pulseScale;

      const centerX = width / 2 + Math.sin(time * 8) * 40;
      const centerY = height * 1.1 + Math.cos(time * 5) * 20;
      const glowRadius = currentRadius * 2.0;

      // Animated glow hues — cycle through blue, green, white
      const blueHue = 210 + Math.sin(time * 3) * 20;
      const greenHue = 157 + Math.sin(time * 4 + 2) * 20;
      const whiteAlpha = 0.15 + Math.sin(time * 5 + 4) * 0.15;

      const blueAlpha = 0.25 + Math.sin(time * 3) * 0.2;
      const greenAlpha = 0.2 + Math.sin(time * 4 + 2) * 0.2;

      const blueOffsetX = Math.sin(time * 2) * glowRadius * 0.3;
      const greenOffsetX = Math.cos(time * 2.5) * glowRadius * 0.3;

      // Blue glow
      const blueGradient = ctx!.createRadialGradient(
        centerX + blueOffsetX,
        centerY,
        0,
        centerX + blueOffsetX,
        centerY,
        glowRadius,
      );
      blueGradient.addColorStop(
        0,
        `hsla(${blueHue}, 80%, 70%, ${blueAlpha.toFixed(2)})`,
      );
      blueGradient.addColorStop(
        0.5,
        `hsla(${blueHue}, 70%, 80%, ${(blueAlpha * 0.33).toFixed(2)})`,
      );
      blueGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

      ctx!.fillStyle = blueGradient;
      ctx!.beginPath();
      ctx!.arc(centerX + blueOffsetX, centerY, glowRadius, 0, Math.PI * 2);
      ctx!.fill();

      // Green glow
      const greenGradient = ctx!.createRadialGradient(
        centerX + greenOffsetX,
        centerY,
        0,
        centerX + greenOffsetX,
        centerY,
        glowRadius * 0.8,
      );
      greenGradient.addColorStop(
        0,
        `hsla(${greenHue}, 75%, 65%, ${greenAlpha.toFixed(2)})`,
      );
      greenGradient.addColorStop(
        0.5,
        `hsla(${greenHue}, 65%, 75%, ${(greenAlpha * 0.3).toFixed(2)})`,
      );
      greenGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

      ctx!.fillStyle = greenGradient;
      ctx!.beginPath();
      ctx!.arc(
        centerX + greenOffsetX,
        centerY,
        glowRadius * 0.8,
        0,
        Math.PI * 2,
      );
      ctx!.fill();

      // White glow (center)
      const whiteGradient = ctx!.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        glowRadius * 0.5,
      );
      whiteGradient.addColorStop(
        0,
        `hsla(0, 0%, 100%, ${whiteAlpha.toFixed(2)})`,
      );
      whiteGradient.addColorStop(
        0.5,
        `hsla(0, 0%, 100%, ${(whiteAlpha * 0.27).toFixed(2)})`,
      );
      whiteGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

      ctx!.fillStyle = whiteGradient;
      ctx!.beginPath();
      ctx!.arc(centerX, centerY, glowRadius * 0.5, 0, Math.PI * 2);
      ctx!.fill();

      ry -= 0.00105;

      const cosRx = Math.cos(rx);
      const sinRx = Math.sin(rx);
      const cosRy = Math.cos(ry);
      const sinRy = Math.sin(ry);

      let renderCount = 0;

      for (const p of particles) {
        const px = p.x * sphereRadius * pulseScale;
        const py = p.y * sphereRadius * pulseScale;
        const pz = p.z * sphereRadius * pulseScale;

        const x1 = px * cosRy + pz * sinRy;
        const z1 = -px * sinRy + pz * cosRy;
        const y1 = py;

        const x2 = x1;
        const y2 = y1 * cosRx - z1 * sinRx;
        const z2 = y1 * sinRx + z1 * cosRx;

        const zTranslated = z2 + FOCAL_LENGTH;

        if (zTranslated > 0) {
          const scale = FOCAL_LENGTH / zTranslated;
          const proj = projected[renderCount]!;
          proj.x = (width / 2 + x2 * scale) | 0;
          proj.y = (height * 1.1 + y2 * scale) | 0;
          proj.z = z2;
          proj.scale = scale;
          proj.size = p.size;
          proj.char = p.char;
          proj.isBlurred = p.isBlurred;
          renderCount++;
        }
      }

      const activeProjected = projected.slice(0, renderCount);
      activeProjected.sort((a, b) => b.z - a.z);

      ctx!.textAlign = 'center';
      ctx!.textBaseline = 'middle';

      for (const p of activeProjected) {
        let alpha = Math.max(
          0.05,
          Math.min(1, (1 - p.z / currentRadius) / 2 + 0.15),
        );
        alpha *= 0.34;

        if (p.isBlurred) {
          alpha *= 0.25;
        }

        ctx!.fillStyle = `rgba(50, 94, 119, ${alpha.toFixed(2)})`;
        const fontSize = Math.max(1, p.size * p.scale) | 0;
        const fontStr = `bold ${fontSize}px monospace`;
        if (ctx!.font !== fontStr) ctx!.font = fontStr;
        ctx!.fillText(p.char, p.x, p.y);
      }

      animationId = requestAnimationFrame(animate);
    }

    // Text rolling logic
    let currentTextIndex = 0;
    textOverlay.innerHTML = getFormattedHTML(ROLLING_TEXTS[currentTextIndex]!);

    let activeShuffleInterval: ReturnType<typeof setInterval> | null = null;

    const textInterval = setInterval(() => {
      currentTextIndex = (currentTextIndex + 1) % ROLLING_TEXTS.length;
      const targetText = ROLLING_TEXTS[currentTextIndex]!;
      const oldText = textOverlay.textContent || '';
      const maxLength = Math.max(oldText.length, targetText.length);
      const highlightLen = getHighlightLength(targetText);

      let iteration = 0;
      const maxIterations = 40;

      if (activeShuffleInterval !== null) clearInterval(activeShuffleInterval);

      activeShuffleInterval = setInterval(() => {
        let result = '';
        for (let i = 0; i < maxLength; i++) {
          const settleIteration = (maxIterations / maxLength) * i;
          if (iteration >= settleIteration) {
            result += targetText[i] || '';
          } else {
            result +=
              SHUFFLE_CHARS[Math.floor(Math.random() * SHUFFLE_CHARS.length)];
          }
        }

        textOverlay.innerHTML = getFormattedShuffleHTML(result, highlightLen);

        if (iteration >= maxIterations) {
          clearInterval(activeShuffleInterval!);
          activeShuffleInterval = null;
          textOverlay.innerHTML = getFormattedHTML(targetText);
        }
        iteration++;
      }, 30);
    }, 5000);

    resize();
    initParticles();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);

    if (motionQuery.matches) {
      // Render a single static frame for users who prefer reduced motion
      animate();
      cancelAnimationFrame(animationId);
    } else {
      animate();
    }

    return () => {
      cancelAnimationFrame(animationId);
      clearInterval(textInterval);
      if (activeShuffleInterval !== null) clearInterval(activeShuffleInterval);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className="relative size-full overflow-hidden">
      <div
        ref={textRef}
        className="text-h2-md text-navy md:text-h1-xl pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-center font-sans font-semibold"
        style={{
          textShadow:
            '0 0 15px rgba(255,255,255,1), 0 0 5px rgba(255,255,255,0.8)',
        }}
      />
      <canvas ref={canvasRef} className="block size-full" />
    </div>
  );
}
